import { CreativeActionCampaignFrontendAsset } from "./CreativeActionCampaignFrontendAsset";
import { DisplayActionCampaignFrontendAsset } from "./DisplayActionCampaignFrontendAsset";
import { isBuiltCampaignFrontendController, } from "../BuiltCampaignFrontendController";
import { isBuiltCampaignControllerTriggerEvent, } from "../BuiltCampaignControllerTriggerEvent";
import { useCampaignBuiltComponents } from "src/shared/store/component-built/composables/useCampaignBuiltComponents";
import { useRoleV3BuiltComponents } from "src/shared/store/component-built/model/role-v3/useRoleV3BuiltComponents";
import { useJourneyV3BuiltComponents } from "src/shared/store/component-built/model/journey-v3/useJourneyV3BuiltComponents";
import { compareDeweyDecimal } from "src/shared/tools/dewy-decimal/DewyDecimal";
const v9CategoryOrder = {};
const getComponent = (action, campaign) => {
    var _a;
    const rootComponentId = (_a = campaign.components.find((component) => component.name === "root")) === null || _a === void 0 ? void 0 : _a.id;
    const firstReferencedComponentId = action.component_ids[0];
    if (!firstReferencedComponentId) {
        return undefined;
    }
    const foundComponent = campaign.components.find((component) => component.id === firstReferencedComponentId &&
        component.id !== rootComponentId);
    return foundComponent ? foundComponent : undefined;
};
const getEventTrigger = (step) => {
    if (!step) {
        return undefined;
    }
    const trigger = step.triggers.find((trigger) => isBuiltCampaignControllerTriggerEvent(trigger));
    if (!trigger) {
        return undefined;
    }
    if (isBuiltCampaignControllerTriggerEvent(trigger)) {
        return trigger;
    }
    return undefined;
};
const getRoleComponents = (campaignValue) => {
    const roleComponents = useRoleV3BuiltComponents(campaignValue.campaign_id).roleV3BuiltComponents;
    return sortComponents(roleComponents.value);
};
const getJourneyComponents = (roleComponents) => {
    const supportedTypes = ["journey-v9.0"];
    let journeyComponents = [];
    roleComponents.forEach((roleComponent) => {
        const components = useJourneyV3BuiltComponents(roleComponent).journeyV3BuiltComponents
            .value;
        journeyComponents = journeyComponents.concat(components);
    });
    return sortComponents(journeyComponents.filter((component) => {
        const type = component.getType();
        return type !== null && supportedTypes.includes(type);
    }));
};
const sortComponents = (components) => {
    return [...components].sort((a, b) => compareDeweyDecimal(a.getSequence(), b.getSequence()));
};
const getV9ComponentCategories = (campaign) => {
    const v9ComponentCategories = [];
    const { builtComponents } = useCampaignBuiltComponents(campaign.campaign_id);
    const roleV9BuiltComponents = getRoleComponents(campaign);
    if (roleV9BuiltComponents.length === 0) {
        return [];
    }
    const journeyV9BuiltComponents = getJourneyComponents(roleV9BuiltComponents);
    const supportedTypes = [
        "content-v9.0",
        "action-v9.0",
        "reward-email-v9.0",
        "email-promotion-v9.0",
    ];
    const visited = new Set();
    const processComponent = (component, category) => {
        if (visited.has(component.id))
            return;
        visited.add(component.id);
        component.listSockets().forEach((socket) => {
            socket.getInstalledComponentIds().forEach((socketedComponentId) => {
                const socketedComponent = builtComponents.value.find((c) => c.id === socketedComponentId);
                if (!socketedComponent || socketedComponent.type === null)
                    return;
                if (supportedTypes.includes(socketedComponent.type)) {
                    v9ComponentCategories.push({
                        id: socketedComponent.id,
                        category: category,
                    });
                }
                else {
                    processComponent(socketedComponent, category);
                }
            });
        });
    };
    journeyV9BuiltComponents.forEach((component) => {
        const [category] = component.getJourneyNames();
        addCategoryToV9Order(category);
        processComponent(component.getBuiltComponent(), category);
    });
    return v9ComponentCategories;
};
export const getCampaignFrontendAssets = (campaign) => {
    const assetsMap = new Map();
    let v9ComponentCategories = [];
    if (campaign.isV9()) {
        v9ComponentCategories = getV9ComponentCategories(campaign);
    }
    campaign.getCreativeActions().forEach((action) => {
        const step = campaign.getStep(action.context.stepId);
        const component = getComponent(action, campaign);
        if (step && isBuiltCampaignFrontendController(step)) {
            const trigger = getEventTrigger(step);
            if (!trigger) {
                return;
            }
            if (campaign.isV9()) {
                const v9Category = v9ComponentCategories.find((category) => category.id === (component === null || component === void 0 ? void 0 : component.id));
                if (v9Category) {
                    step.category = v9Category.category;
                }
            }
            const asset = new CreativeActionCampaignFrontendAsset({
                step,
                trigger,
                action,
                component,
                isLegacyCampaign: campaign.isLegacyCampaign(),
                campaignVersion: campaign.getCampaignVersion(),
            });
            assetsMap.set(asset.getAssetHandle(), asset);
        }
    });
    campaign.getDisplayActions().forEach((action) => {
        const step = campaign.getStep(action.context.stepId);
        const component = getComponent(action, campaign);
        if (step && isBuiltCampaignFrontendController(step) && component) {
            const trigger = getEventTrigger(step);
            if (!trigger) {
                return;
            }
            if (campaign.isV9()) {
                const v9Category = v9ComponentCategories.find((category) => category.id === (component === null || component === void 0 ? void 0 : component.id));
                if (v9Category) {
                    step.category = v9Category.category;
                }
            }
            const asset = new DisplayActionCampaignFrontendAsset({
                step,
                trigger,
                action,
                component,
                isLegacyCampaign: campaign.isLegacyCampaign(),
                campaignVersion: campaign.getCampaignVersion(),
            });
            assetsMap.set(asset.getAssetHandle(), asset);
        }
    });
    return Array.from(assetsMap.values());
};
export const addCategoryToV9Order = (category) => {
    const formattedCategory = category.toUpperCase();
    if (!v9CategoryOrder[formattedCategory]) {
        v9CategoryOrder[formattedCategory] =
            Object.keys(v9CategoryOrder).length + 1;
    }
};
export const getV9CategoryOrder = () => {
    return v9CategoryOrder;
};
